import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { User, ROLE_LABELS } from '../types/user';

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
  user: User | null;
  // Role and active-state are ADMIN-only; a USER caller manages USER accounts
  // and cannot assign roles (the backend also enforces this).
  isAdmin: boolean;
}

const UserDialog: React.FC<UserDialogProps> = ({ open, onClose, onSave, user, isAdmin }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'USER' as 'ADMIN' | 'USER',
    is_active: true,
  });
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        is_active: user.is_active,
      });
    } else {
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'USER',
        is_active: true,
      });
    }
    setErrors({});
  }, [user, open]);

  const handleChange = (e: any) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_active' ? checked : value,
    });
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.email) {
      newErrors.email = 'L\'email è obbligatoria';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }

    if (!formData.name) {
      newErrors.name = 'Il nome è obbligatorio';
    }

    if (!user && !formData.password) {
      newErrors.password = 'La password è obbligatoria';
    } else if (!user && formData.password.length < 8) {
      newErrors.password = 'La password deve essere di almeno 8 caratteri';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      const userData: any = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active,
      };

      if (!user && formData.password) {
        userData.password = formData.password;
      }

      onSave(userData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{user ? 'Modifica utente' : 'Nuovo utente'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            name="name"
            label="Nome"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
          {!user && (
            <TextField
              name="password"
              label="Password"
              type="password"
              fullWidth
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
            />
          )}
          {isAdmin && (
            <FormControl fullWidth>
              <InputLabel>Ruolo</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="Ruolo"
                onChange={handleChange}
              >
                <MenuItem value="USER">{ROLE_LABELS.USER}</MenuItem>
                <MenuItem value="ADMIN">{ROLE_LABELS.ADMIN}</MenuItem>
              </Select>
            </FormControl>
          )}
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              }
              label="Attivo"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} variant="contained">
          {user ? 'Salva' : 'Crea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;